import React, { useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { Invitees } from '@atlassian/invite-prompt';
import { InvitePromptFlag } from '@atlassian/invite-prompt';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';

import {
	incrementDismissalCount,
	setUserOptOutTrue,
	shouldShowOptOutOption,
} from '../services/opt-out';
import { useInviteUsers } from '../services/invite-users/invite-users';

import { useInviteSuccessFlag } from './useInviteSuccessFlag';
import { useInviteErrorFlag } from './useInviteErrorFlag';

const PRODUCT = 'jira-software';
const MAX_NUMBER_OF_INVITEES = 10;

export const useInvitePromptFlag = (flags?: FlagsStateContainer) => {
	const inviteUsers = useInviteUsers();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const showSuccessFlag = useInviteSuccessFlag(flags);
	const showErrorFlag = useInviteErrorFlag(flags);
	return useCallback(
		(
			invitees: Invitees,
			targetTitle: string,
			continueUrl: string,
			jiraCloudId: string,
			pageVersion: number,
		) => {
			const attributes = {
				invitees: invitees.length,
				pageVersion,
			};

			const onDismissFlag = (attributes: any) => {
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'dismissFlag',
						source: 'inviteOnLinkInsert',
						attributes,
					},
				}).fire();
				incrementDismissalCount();
			};

			const onOptOutFlag = (id: string | number, attributes: any, flags?: FlagsStateContainer) => {
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'optOutFlag',
						source: 'inviteOnLinkInsert',
						attributes,
					},
				}).fire();
				flags?.hideFlag(id);
				setUserOptOutTrue();
			};

			flags?.showFlag({
				customComponent: (props) => (
					<InvitePromptFlag
						{...props}
						id={props.id}
						invitees={invitees.slice(0, MAX_NUMBER_OF_INVITEES)}
						targetTitle={targetTitle}
						targetProduct={PRODUCT}
						onDismiss={() => {
							onDismissFlag(attributes);
						}}
						onOptOut={
							shouldShowOptOutOption()
								? () => {
										onOptOutFlag(props.id, attributes, flags);
									}
								: undefined
						}
						onSubmit={async () => {
							try {
								await inviteUsers({
									continueUrl,
									userIds: invitees.map(({ id }) => id),
									tenantId: jiraCloudId,
									product: PRODUCT,
								});
								flags.hideFlag(props.id);
								showSuccessFlag(invitees);
							} catch {
								flags.hideFlag(props.id);
								showErrorFlag();
							}
							createAnalyticsEvent({
								type: 'sendUIEvent',
								data: {
									action: 'clicked',
									actionSubject: 'button',
									actionSubjectId: 'addPeople',
									source: 'inviteOnLinkInsert',
									attributes,
								},
							}).fire();
						}}
					/>
				),
			});

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'shown',
					actionSubject: 'flag',
					actionSubjectId: 'invitePromptFlag',
					source: 'inviteOnLinkInsert',
					attributes,
				},
			}).fire();
		},
		[createAnalyticsEvent, flags, inviteUsers, showErrorFlag, showSuccessFlag],
	);
};
